import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, ArrowLeft } from "lucide-react";

// --- Animation Variants ---
const pageVariants = {
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  exit: { opacity: 0, y: 50, transition: { duration: 0.3 } },
};

const formVariants = {
  initial: { scale: 0.9, opacity: 0 },
  animate: { scale: 1, opacity: 1, transition: { delay: 0.2, duration: 0.5 } },
};

function ContactPage() {
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    alert("Thank you for your message! We will get back to you shortly.");
    e.target.reset();
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - DairyKhataBook</title>
      </Helmet>

      <motion.div
        className="relative w-full min-h-screen bg-gradient-to-br from-indigo-50 to-white overflow-hidden flex flex-col items-center p-4 sm:p-8"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {/* Decorative Shapes */}
        <div className="absolute top-[-100px] left-[-100px] w-80 h-80 bg-indigo-300/50 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute bottom-[-80px] right-[-80px] w-72 h-72 bg-pink-300/50 rounded-full opacity-20 blur-3xl"></div>

        {/* Back Button */}
        <header className="w-full max-w-4xl pt-4 pb-8 z-10">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-indigo-600 hover:text-indigo-800 transition font-medium"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
        </header>

        {/* Contact Form and Info */}
        <motion.div
          className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl p-6 md:p-12 z-10 grid grid-cols-1 lg:grid-cols-2 gap-10"
          variants={formVariants}
        >
          {/* Left Side: Contact Info */}
          <div className="flex flex-col justify-center space-y-6 lg:order-1">
            <h2 className="text-3xl font-bold text-indigo-700 mb-4 border-b pb-2 border-indigo-100">
              Contact Information
            </h2>

            <ContactInfoItem
              icon={Mail}
              title="Email"
              detail="dairykhatabook@gmail.com"
            />
          </div>

          {/* Right Side: Contact Form */}
          <div className="lg:order-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-2 border-indigo-100">
              Send a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <InputGroup label="Full Name" type="text" id="name" />
              <InputGroup label="Email Address" type="email" id="email" />
              <InputGroup label="Subject" type="text" id="subject" />

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Your Message
                </label>
                <textarea
                  id="message"
                  rows="4"
                  required
                  className="mt-1 block w-full rounded-lg border border-gray-300 p-3 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition text-lg"
                ></textarea>
              </div>

              <motion.button
                type="submit"
                className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-indigo-600 text-white rounded-xl font-semibold text-lg hover:bg-indigo-700 transition duration-300 shadow-lg shadow-indigo-300/50"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
              >
                <Send className="w-5 h-5" />
                <span>Send Message</span>
              </motion.button>
            </form>
          </div>
        </motion.div>

        {/* Footer */}
        <footer className="mt-auto py-4 text-gray-500 text-sm">
          © {new Date().getFullYear()} DairyKhataBook. All rights reserved.
        </footer>
      </motion.div>
    </>
  );
}

// Reusable Input Component
const InputGroup = ({ label, type, id }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input
      type={type}
      id={id}
      name={id}
      required
      className="mt-1 block w-full rounded-lg border border-gray-300 p-3 shadow-sm text-lg focus:border-indigo-500 focus:ring-indigo-500 transition"
    />
  </div>
);

// Reusable Contact Info Item Component
const ContactInfoItem = ({ icon: Icon, title, detail }) => (
  <div className="flex items-start space-x-4">
    <div className="p-3 bg-indigo-100 rounded-full flex-shrink-0">
      <Icon className="w-6 h-6 text-indigo-600" />
    </div>
    <div>
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-lg">{detail}</p>
    </div>
  </div>
);

export default ContactPage;
